// SuperAdminLayout.tsx - Layout para páginas de Super Admin (tema claro)
import React, { ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    Building2,
    DollarSign,
    BarChart3,
    Settings,
    LogOut,
    User,
    Menu,
    X
} from 'lucide-react';
import { useAuth } from '../shared/hooks/useAuth';
import './platformTheme.css';

// Color de la plataforma (estilo ITOBOX Platform 2.0)
const BRAND = '#E50914';

interface SuperAdminLayoutProps {
    children: ReactNode;
    title?: string;
    subtitle?: string;
}

const SuperAdminLayout: React.FC<SuperAdminLayoutProps> = ({ children, title, subtitle }) => {
    const location = useLocation();
    const { user, logout } = useAuth();
    const [menuOpen, setMenuOpen] = useState(false);

    const menuItems = [
        { path: '/super-admin', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/super-admin/tenants', icon: Building2, label: 'Tenants' },
        { path: '/billing', icon: DollarSign, label: 'Facturación' },
        { path: '/analytics', icon: BarChart3, label: 'Analíticas' },
        { path: '/settings', icon: Settings, label: 'Configuración' },
    ];

    const isActive = (path: string) => {
        return location.pathname === path;
    };

    return (
        <div className="super-admin-layout">
            {/* Barra superior móvil */}
            <div className="mobile-topbar">
                <button
                    type="button"
                    className="mobile-menu-btn"
                    onClick={() => setMenuOpen(true)}
                    aria-label="Abrir menú"
                >
                    <Menu size={22} />
                </button>
                <span className="mobile-topbar-title">Super Admin</span>
            </div>

            {/* Overlay móvil */}
            {menuOpen && (
                <div className="sidebar-overlay" onClick={() => setMenuOpen(false)} aria-hidden="true" />
            )}

            {/* Sidebar */}
            <aside className={`sidebar ${menuOpen ? 'sidebar-open' : ''}`}>
                <div className="sidebar-header">
                    <button
                        type="button"
                        className="sidebar-close-btn"
                        onClick={() => setMenuOpen(false)}
                        aria-label="Cerrar menú"
                    >
                        <X size={18} />
                    </button>
                    <h2>Super Admin</h2>
                    <p>Sistema Courier SaaS</p>
                </div>

                <nav className="sidebar-nav">
                    {menuItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setMenuOpen(false)}
                            className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                        >
                            <item.icon size={20} />
                            <span>{item.label}</span>
                        </Link>
                    ))}
                </nav>

                <div className="sidebar-footer">
                    <div className="user-info">
                        <User size={20} />
                        <div>
                            <div className="user-name">{user?.email?.split('@')[0]}</div>
                            <div className="user-role">Super Admin</div>
                        </div>
                    </div>
                    <button onClick={logout} className="logout-btn" title="Cerrar sesión">
                        <LogOut size={18} />
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="main-content">
                {title && (
                    <header className="page-header">
                        <div>
                            <h1>{title}</h1>
                            {subtitle && <p>{subtitle}</p>}
                        </div>
                    </header>
                )}

                <div className="page-content p2" style={{ ['--p2-brand' as any]: BRAND }}>
                    {children}
                </div>
            </main>

            <style>{`
        .super-admin-layout {
          display: flex;
          min-height: 100vh;
          background: #F5F6F8;
          font-family: Poppins, system-ui, sans-serif;
        }

        .sidebar {
          width: 240px;
          background: white;
          border-right: 1px solid #EEF0F3;
          display: flex;
          flex-direction: column;
          position: fixed;
          height: 100vh;
          left: 0;
          top: 0;
        }

        .sidebar-header {
          position: relative;
          padding: 24px 20px;
          border-bottom: 1px solid #EEF0F3;
        }

        .sidebar-close-btn {
          display: none;
        }

        .mobile-topbar {
          display: none;
        }

        .sidebar-overlay {
          display: none;
        }

        .sidebar-header h2 {
          font-size: 18px;
          font-weight: 800;
          letter-spacing: -0.01em;
          margin: 0;
          color: #111111;
        }

        .sidebar-header p {
          font-size: 12px;
          color: #6B7280;
          margin: 4px 0 0 0;
        }

        .sidebar-nav {
          flex: 1;
          padding: 16px;
          overflow-y: auto;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          border-radius: 12px;
          color: #6B7280;
          text-decoration: none;
          transition: all 0.2s;
          margin-bottom: 4px;
          font-size: 14px;
          font-weight: 500;
        }

        .nav-item:hover {
          background: #F5F6F8;
          color: #111111;
        }

        .nav-item.active {
          background: color-mix(in srgb, #E50914 8%, white);
          color: #E50914;
          font-weight: 600;
        }

        .sidebar-footer {
          padding: 16px;
          border-top: 1px solid #EEF0F3;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
          color: #6B7280;
        }

        .user-name {
          font-size: 14px;
          font-weight: 500;
          color: #111111;
        }

        .user-role {
          font-size: 12px;
          color: #6B7280;
        }

        .logout-btn {
          background: none;
          border: none;
          color: #6B7280;
          cursor: pointer;
          padding: 8px;
          border-radius: 4px;
          transition: all 0.2s;
        }

        .logout-btn:hover {
          background: #F5F6F8;
          color: #111111;
        }

        .main-content {
          flex: 1;
          margin-left: 240px;
          display: flex;
          flex-direction: column;
        }

        .page-header {
          padding: 24px 32px;
          background: white;
          border-bottom: 1px solid #EEF0F3;
        }

        .page-header h1 {
          font-size: 24px;
          font-weight: 600;
          margin: 0;
          color: #111111;
        }

        .page-header p {
          color: #6B7280;
          margin: 4px 0 0 0;
          font-size: 14px;
        }

        .page-content {
          flex: 1;
          padding: 0;
          overflow-y: auto;
        }

        @media (max-width: 1024px) {
          .mobile-topbar {
            display: flex;
            align-items: center;
            gap: 12px;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            height: 56px;
            background: white;
            border-bottom: 1px solid #EEF0F3;
            padding: 0 16px;
            z-index: 45;
          }

          .mobile-menu-btn {
            background: none;
            border: none;
            color: #111111;
            padding: 8px;
            border-radius: 4px;
            cursor: pointer;
            display: flex;
          }

          .mobile-menu-btn:hover {
            background: #F5F6F8;
          }

          .mobile-topbar-title {
            font-size: 16px;
            font-weight: 700;
            color: #111111;
          }

          .sidebar-overlay {
            display: block;
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.4);
            z-index: 55;
          }

          .sidebar {
            width: 260px;
            max-width: 85vw;
            transform: translateX(-100%);
            transition: transform 0.25s ease-in-out;
            z-index: 60;
          }

          .sidebar.sidebar-open {
            transform: translateX(0);
          }

          .sidebar-close-btn {
            display: flex;
            position: absolute;
            top: 16px;
            right: 12px;
            background: rgba(0, 0, 0, 0.05);
            border: none;
            padding: 6px;
            border-radius: 6px;
            cursor: pointer;
            color: #111111;
          }

          .main-content {
            margin-left: 0;
            padding-top: 56px;
          }

          .page-header {
            padding: 16px 20px;
          }

          .page-header h1 {
            font-size: 20px;
          }
        }
      `}</style>
        </div>
    );
};

export default SuperAdminLayout;
